import { API_BASE } from '../config/api';

/**
 * Platform API client. Pass access_token for app routes (profile, meetups, posts create, etc.).
 */
async function request<T>(
  path: string,
  opts?: { method?: string; body?: unknown; token?: string }
): Promise<T> {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const method = opts?.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts?.token) headers['Authorization'] = `Bearer ${opts.token}`;
  if (method !== 'GET' && opts?.body) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, {
    method,
    headers,
    body: method !== 'GET' && opts?.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.json();
}

export const api = {
  health: () => request<{ ok: boolean }>('/health'),
  cities: {
    list: (opts?: { region?: string; limit?: number }, token?: string) => {
      const params = new URLSearchParams();
      if (opts?.region) params.set('region', opts.region);
      if (opts?.limit) params.set('limit', String(opts.limit));
      const q = params.toString();
      return request<{ items: import('../types/api').City[]; nextToken?: string }>(
        `/cities${q ? `?${q}` : ''}`,
        { token }
      );
    },
    top: (limit = 10, token?: string) =>
      request<{ items: { cityId: string; totalScore: number }[] }>(`/cities/top?limit=${limit}`, { token }),
    get: (cityId: string, token?: string) =>
      request<import('../types/api').CityDetail>(`/cities/${encodeURIComponent(cityId)}`, { token }),
  },
  posts: {
    list: (opts?: { type?: string; tag?: string; cityId?: string; limit?: number }, token?: string) => {
      const params = new URLSearchParams();
      if (opts?.type) params.set('type', opts.type);
      if (opts?.tag) params.set('tag', opts.tag);
      if (opts?.cityId) params.set('cityId', opts.cityId);
      if (opts?.limit) params.set('limit', String(opts.limit));
      const q = params.toString();
      return request<{ items: import('../types/api').Post[]; nextToken?: string }>(
        `/posts${q ? `?${q}` : ''}`,
        { token }
      );
    },
    get: (postId: string, token?: string) =>
      request<import('../types/api').PostDetail>(`/posts/${encodeURIComponent(postId)}`, { token }),
  },
};
